import React, { useState, useEffect } from 'react';
import { Cog6ToothIcon, ArrowDownTrayIcon, XMarkIcon, DocumentArrowDownIcon, TableCellsIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const ReportingAnalytics = () => {
  const [dateRange, setDateRange] = useState('7d');
  const [reportType, setReportType] = useState('all');
  const [showConfigModal, setShowConfigModal] = useState<boolean>(false);
  const [selectedMetrics, setSelectedMetrics] = useState<string[]>([
    'users',
    'sessions',
    'messagesSent',
    'deliveryRate'
  ]);
  const [deliveryDateRange, setDeliveryDateRange] = useState('24h');
  const [timeInterval, setTimeInterval] = useState('5m');
  const [sentRange, setSentRange] = useState('all');
  const [failureRange, setFailureRange] = useState('all');
  const [successRange, setSuccessRange] = useState('all');
  const [showExportOptions, setShowExportOptions] = useState<boolean>(false);
  const [campaignDateRange, setCampaignDateRange] = useState('24h');
  const [campaignInterval, setCampaignInterval] = useState('5m');
  const [activeFilter, setActiveFilter] = useState('all');
  const [reachFilter, setReachFilter] = useState('all');
  const [engagementFilter, setEngagementFilter] = useState('all');
  const [conversionFilter, setConversionFilter] = useState('all');
  const [showCampaignExportOptions, setShowCampaignExportOptions] = useState<boolean>(false);
  const [financialDateRange, setFinancialDateRange] = useState('24h');
  const [financialInterval, setFinancialInterval] = useState('5m');
  const [revenueFilter, setRevenueFilter] = useState('all');
  const [expensesFilter, setExpensesFilter] = useState('all');
  const [profitFilter, setProfitFilter] = useState('all');
  const [growthFilter, setGrowthFilter] = useState('all');
  const [showFinancialExportOptions, setShowFinancialExportOptions] = useState<boolean>(false);

  const handleExport = (type: 'pdf' | 'csv' | 'update') => {
    switch (type) {
      case 'pdf':
        console.log('Exporting as PDF...');
        break;
      case 'csv':
        console.log('Exporting as CSV...');
        break;
      case 'update':
        console.log('Updating timeline...');
        break;
    }
    setShowExportOptions(false);
  };

  const handleCampaignExport = (type: 'pdf' | 'csv' | 'update') => {
    switch (type) {
      case 'pdf':
        console.log('Exporting campaign data as PDF...');
        break;
      case 'csv':
        console.log('Exporting campaign data as CSV...');
        break;
      case 'update':
        console.log('Updating campaign data...');
        break;
    }
    setShowCampaignExportOptions(false);
  };

  const handleFinancialExport = (type: 'pdf' | 'csv' | 'update') => {
    switch (type) {
      case 'pdf':
        console.log('Exporting financial data as PDF...');
        break;
      case 'csv':
        console.log('Exporting financial data as CSV...');
        break;
      case 'update':
        console.log('Updating financial data...');
        break;
    }
    setShowFinancialExportOptions(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showExportOptions && !(event.target as Element).closest('.relative')) {
        setShowExportOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showExportOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showCampaignExportOptions && !(event.target as Element).closest('.relative')) {
        setShowCampaignExportOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCampaignExportOptions]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFinancialExportOptions && !(event.target as Element).closest('.relative')) {
        setShowFinancialExportOptions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFinancialExportOptions]);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-medium mb-6">Reporting and Analytics</h1>

      {/* Section 1: SMS Analytics Overview */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-medium">SMS Analytics Overview</h2>
          <button 
            onClick={() => setShowConfigModal(true)}
            className="text-[#FF5722] hover:text-[#FF5722]/90 text-sm flex items-center"
          >
            <Cog6ToothIcon className="w-5 h-5 mr-1" />
            Configure Analytics
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Active Users</h3>
            <div className="text-2xl font-bold text-[#FF5722]">15,678</div>
            <p className="text-sm text-gray-600">+12.3% vs last period</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Messages Sent</h3>
            <div className="text-2xl font-bold text-[#FF5722]">234.5K</div>
            <p className="text-sm text-gray-600">+8.7% vs last period</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Delivery Rate</h3>
            <div className="text-2xl font-bold text-[#FF5722]">98.7%</div>
            <p className="text-sm text-gray-600">+1.5% vs last period</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Avg Response Time</h3>
            <div className="text-2xl font-bold text-[#FF5722]">2.4s</div>
            <p className="text-sm text-gray-600">-0.3s vs last period</p>
          </div>
        </div>

        {/* Analytics Chart */}
        <div className="h-64 bg-gray-50 rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium">Performance Trends</h3>
            <div className="flex space-x-2">
              <select className="border rounded-md px-2 py-1 text-sm">
                <option>Hourly</option>
                <option>Daily</option>
                <option>Weekly</option>
                <option>Monthly</option>
              </select>
              <button className="text-[#FF5722] p-1 hover:bg-[#FF5722]/10 rounded">
                <ArrowDownTrayIcon className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="h-48 flex items-center justify-center">
            <p className="text-gray-500">Analytics Chart</p>
          </div>
        </div>
      </div>

      {/* Analytics Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-medium">Configure Analytics</h3>
              <button 
                onClick={() => setShowConfigModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Metrics Selection */}
              <div>
                <h4 className="font-medium mb-3">Choose Metrics to Display</h4>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { id: 'users', label: 'Active Users' },
                    { id: 'messagesSent', label: 'Messages Sent' },
                    { id: 'deliveryRate', label: 'Delivery Rate' },
                    { id: 'responseTime', label: 'Response Time' },
                    { id: 'failureRate', label: 'Failure Rate' },
                    { id: 'costPerMessage', label: 'Cost per Message' },
                    { id: 'campaignPerformance', label: 'Campaign Performance' },
                    { id: 'userEngagement', label: 'User Engagement' }
                  ].map((metric) => (
                    <label key={metric.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={selectedMetrics.includes(metric.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedMetrics([...selectedMetrics, metric.id]);
                          } else {
                            setSelectedMetrics(selectedMetrics.filter(m => m !== metric.id));
                          }
                        }}
                        className="rounded text-[#FF5722] focus:ring-[#FF5722]"
                      />
                      <span>{metric.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Data Range */}
              <div>
                <h4 className="font-medium mb-3">Default Date Range</h4>
                <select className="w-full border rounded-md px-3 py-2">
                  <option>Last 24 Hours</option>
                  <option>Last 7 Days</option>
                  <option>Last 30 Days</option>
                  <option>Custom Range</option>
                </select>
              </div>

              {/* Chart Preferences */}
              <div>
                <h4 className="font-medium mb-3">Chart Preferences</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm mb-1">Chart Type</label>
                    <select className="w-full border rounded-md px-3 py-2">
                      <option>Line Chart</option>
                      <option>Bar Chart</option>
                      <option>Area Chart</option>
                      <option>Combined</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm mb-1">Update Frequency</label>
                    <select className="w-full border rounded-md px-3 py-2">
                      <option>Real-time</option>
                      <option>Every 5 minutes</option>
                      <option>Every 15 minutes</option>
                      <option>Hourly</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Alert Configuration */}
              <div>
                <h4 className="font-medium mb-3">Analytics Alerts</h4>
                <div className="space-y-2">
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded text-[#FF5722] focus:ring-[#FF5722]" />
                    <span>Alert on delivery rate drops below 95%</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded text-[#FF5722] focus:ring-[#FF5722]" />
                    <span>Alert on unusual message volume spikes</span>
                  </label>
                  <label className="flex items-center space-x-2">
                    <input type="checkbox" className="rounded text-[#FF5722] focus:ring-[#FF5722]" />
                    <span>Alert on high failure rates</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowConfigModal(false)}
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  // Save configuration logic here
                  setShowConfigModal(false);
                }}
                className="px-4 py-2 bg-[#FF5722] text-white rounded-md hover:bg-[#FF5722]/90"
              >
                Save Configuration
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Section 2: Delivery Reports */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col space-y-4 mb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium">Delivery Reports</h2>
            <div className="relative">
              <button 
                onClick={() => setShowExportOptions(!showExportOptions)}
                className="text-[#FF5722] hover:text-[#FF5722]/90 text-sm flex items-center"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-1" />
                Export Report
              </button>

              {/* Export Options Dropdown */}
              {showExportOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <div className="py-1">
                    <button
                      onClick={() => handleExport('pdf')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <DocumentArrowDownIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Export as PDF
                    </button>
                    <button
                      onClick={() => handleExport('csv')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <TableCellsIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Export as CSV
                    </button>
                    <div className="border-t border-gray-200"></div>
                    <button
                      onClick={() => handleExport('update')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <ArrowPathIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Update Timeline
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Smart Filters Row */}
          <div className="flex flex-wrap gap-3 items-center bg-gray-50 p-3 rounded-lg">
            {/* Date Range Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Date:</label>
              <select
                value={deliveryDateRange}
                onChange={(e) => setDeliveryDateRange(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="1h">Last Hour</option>
                <option value="6h">Last 6 Hours</option>
                <option value="12h">Last 12 Hours</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Time Interval Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Interval:</label>
              <select
                value={timeInterval}
                onChange={(e) => setTimeInterval(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="5m">5 Minutes</option>
                <option value="15m">15 Minutes</option>
                <option value="30m">30 Minutes</option>
                <option value="1h">1 Hour</option>
                <option value="3h">3 Hours</option>
                <option value="6h">6 Hours</option>
                <option value="12h">12 Hours</option>
                <option value="24h">24 Hours</option>
              </select>
            </div>

            {/* Messages Sent Range Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Sent:</label>
              <select
                value={sentRange}
                onChange={(e) => setSentRange(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt1000">&lt; 1,000</option>
                <option value="1k-10k">1,000 - 10,000</option>
                <option value="10k-50k">10,000 - 50,000</option>
                <option value="gt50k">&gt; 50,000</option>
              </select>
            </div>

            {/* Failed Messages Range Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Failed:</label>
              <select
                value={failureRange}
                onChange={(e) => setFailureRange(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt10">&lt; 10</option>
                <option value="10-50">10 - 50</option>
                <option value="50-100">50 - 100</option>
                <option value="gt100">&gt; 100</option>
              </select>
            </div>

            {/* Success Rate Range Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Success:</label>
              <select
                value={successRange}
                onChange={(e) => setSuccessRange(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt95">&lt; 95%</option>
                <option value="95-97">95% - 97%</option>
                <option value="97-99">97% - 99%</option>
                <option value="gt99">&gt; 99%</option>
              </select>
            </div>

            {/* Reset Filters Button */}
            <button
              onClick={() => {
                setDeliveryDateRange('24h');
                setTimeInterval('5m');
                setSentRange('all');
                setFailureRange('all');
                setSuccessRange('all');
              }}
              className="ml-auto text-sm text-gray-500 hover:text-gray-700"
            >
              Reset Filters
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Total Sent</h3>
            <div className="text-2xl font-bold text-green-600">45,678</div>
            <p className="text-sm text-gray-600">Messages delivered</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Failed</h3>
            <div className="text-2xl font-bold text-red-600">234</div>
            <p className="text-sm text-gray-600">Delivery failed</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Success Rate</h3>
            <div className="text-2xl font-bold text-blue-600">98.5%</div>
            <p className="text-sm text-gray-600">Average delivery rate</p>
          </div>
        </div>

        {/* Delivery Timeline */}
        <div className="border rounded-lg p-4">
          <h3 className="font-medium mb-4">Delivery Timeline</h3>
          <div className="space-y-4">
            {[
              { time: '12:00 PM', sent: 1234, success: 98.7 },
              { time: '1:00 PM', sent: 2345, success: 99.1 },
              { time: '2:00 PM', sent: 1876, success: 97.8 }
            ].map((hour, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <p className="font-medium">{hour.time}</p>
                    <p className="text-sm text-gray-600">{hour.sent} messages</p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-green-600">{hour.success}%</p>
                    <p className="text-sm text-gray-600">Success rate</p>
                  </div>
                </div>
                <div className="h-2 bg-gray-200 rounded-full">
                  <div 
                    className="h-2 bg-green-500 rounded-full" 
                    style={{ width: `${hour.success}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Section 3: Campaign Performance */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col space-y-4 mb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium">Campaign Performance</h2>
            <div className="relative">
              <button 
                onClick={() => setShowCampaignExportOptions(!showCampaignExportOptions)}
                className="text-[#FF5722] hover:text-[#FF5722]/90 text-sm flex items-center"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-1" />
                Export Data
              </button>

              {/* Export Options Dropdown */}
              {showCampaignExportOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <div className="py-1">
                    <button
                      onClick={() => handleCampaignExport('pdf')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <DocumentArrowDownIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Export as PDF
                    </button>
                    <button
                      onClick={() => handleCampaignExport('csv')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <TableCellsIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Export as CSV
                    </button>
                    <div className="border-t border-gray-200"></div>
                    <button
                      onClick={() => handleCampaignExport('update')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <ArrowPathIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Update Data
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Smart Filters Row */}
          <div className="flex flex-wrap gap-3 items-center bg-gray-50 p-3 rounded-lg">
            {/* Date Range Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Date:</label>
              <select
                value={campaignDateRange}
                onChange={(e) => setCampaignDateRange(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="1h">Last Hour</option>
                <option value="6h">Last 6 Hours</option>
                <option value="12h">Last 12 Hours</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Time Interval Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Interval:</label>
              <select
                value={campaignInterval}
                onChange={(e) => setCampaignInterval(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="5m">5 Minutes</option>
                <option value="15m">15 Minutes</option>
                <option value="30m">30 Minutes</option>
                <option value="1h">1 Hour</option>
                <option value="3h">3 Hours</option>
                <option value="6h">6 Hours</option>
                <option value="12h">12 Hours</option>
                <option value="24h">24 Hours</option>
              </select>
            </div>

            {/* Active Campaigns Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Active:</label>
              <select
                value={activeFilter}
                onChange={(e) => setActiveFilter(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt5">&lt; 5</option>
                <option value="5-10">5 - 10</option>
                <option value="10-20">10 - 20</option>
                <option value="gt20">&gt; 20</option>
              </select>
            </div>

            {/* Total Reach Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Reach:</label>
              <select
                value={reachFilter}
                onChange={(e) => setReachFilter(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt1000">&lt; 1,000</option>
                <option value="1k-10k">1,000 - 10,000</option>
                <option value="10k-50k">10,000 - 50,000</option>
                <option value="gt50k">&gt; 50,000</option>
              </select>
            </div>

            {/* Engagement Rate Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Engagement:</label>
              <select
                value={engagementFilter}
                onChange={(e) => setEngagementFilter(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt5">&lt; 5%</option>
                <option value="5-15">5% - 15%</option>
                <option value="15-25">15% - 25%</option>
                <option value="gt25">&gt; 25%</option>
              </select>
            </div>

            {/* Conversion Rate Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Conversion:</label>
              <select
                value={conversionFilter}
                onChange={(e) => setConversionFilter(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt2">&lt; 2%</option>
                <option value="2-5">2% - 5%</option>
                <option value="5-10">5% - 10%</option>
                <option value="gt10">&gt; 10%</option>
              </select>
            </div>

            {/* Reset Filters Button */}
            <button
              onClick={() => {
                setCampaignDateRange('24h');
                setCampaignInterval('5m');
                setActiveFilter('all');
                setReachFilter('all');
                setEngagementFilter('all');
                setConversionFilter('all');
              }}
              className="ml-auto text-sm text-gray-500 hover:text-gray-700"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Rest of the Campaign Performance content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Active Campaigns</h3>
            <div className="text-2xl font-bold text-[#FF5722]">12</div>
            <p className="text-sm text-gray-600">Currently running</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Total Reach</h3>
            <div className="text-2xl font-bold text-[#FF5722]">34,567</div>
            <p className="text-sm text-gray-600">Recipients</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Engagement Rate</h3>
            <div className="text-2xl font-bold text-[#FF5722]">15.4%</div>
            <p className="text-sm text-gray-600">Average</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Conversion Rate</h3>
            <div className="text-2xl font-bold text-[#FF5722]">8.2%</div>
            <p className="text-sm text-gray-600">Average</p>
          </div>
        </div>

        {/* Campaign List */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reach</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Conversion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap">Q4 Promotion</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                    Active
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">12,345</td>
                <td className="px-6 py-4 whitespace-nowrap">18.5%</td>
                <td className="px-6 py-4 whitespace-nowrap">9.2%</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Section 4: Financial Reports */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex flex-col space-y-4 mb-6">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-medium">Financial Reports</h2>
            <div className="relative">
              <button 
                onClick={() => setShowFinancialExportOptions(!showFinancialExportOptions)}
                className="text-[#FF5722] hover:text-[#FF5722]/90 text-sm flex items-center"
              >
                <ArrowDownTrayIcon className="w-5 h-5 mr-1" />
                Export Data
              </button>

              {/* Export Options Dropdown */}
              {showFinancialExportOptions && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200">
                  <div className="py-1">
                    <button
                      onClick={() => handleFinancialExport('pdf')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <DocumentArrowDownIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Export as PDF
                    </button>
                    <button
                      onClick={() => handleFinancialExport('csv')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <TableCellsIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Export as CSV
                    </button>
                    <div className="border-t border-gray-200"></div>
                    <button
                      onClick={() => handleFinancialExport('update')}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <ArrowPathIcon className="w-5 h-5 mr-2 text-[#FF5722]" />
                      Update Data
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Smart Filters Row */}
          <div className="flex flex-wrap gap-3 items-center bg-gray-50 p-3 rounded-lg">
            {/* Date Range Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Date:</label>
              <select
                value={financialDateRange}
                onChange={(e) => setFinancialDateRange(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="1h">Last Hour</option>
                <option value="6h">Last 6 Hours</option>
                <option value="12h">Last 12 Hours</option>
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {/* Time Interval Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Interval:</label>
              <select
                value={financialInterval}
                onChange={(e) => setFinancialInterval(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="5m">5 Minutes</option>
                <option value="15m">15 Minutes</option>
                <option value="30m">30 Minutes</option>
                <option value="1h">1 Hour</option>
                <option value="3h">3 Hours</option>
                <option value="6h">6 Hours</option>
                <option value="12h">12 Hours</option>
                <option value="24h">24 Hours</option>
              </select>
            </div>

            {/* Revenue Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Revenue:</label>
              <select
                value={revenueFilter}
                onChange={(e) => setRevenueFilter(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt1000">&lt; $1,000</option>
                <option value="1k-10k">$1,000 - $10,000</option>
                <option value="10k-50k">$10,000 - $50,000</option>
                <option value="gt50k">&gt; $50,000</option>
              </select>
            </div>

            {/* Expenses Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Expenses:</label>
              <select
                value={expensesFilter}
                onChange={(e) => setExpensesFilter(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt500">&lt; $500</option>
                <option value="500-5k">$500 - $5,000</option>
                <option value="5k-25k">$5,000 - $25,000</option>
                <option value="gt25k">&gt; $25,000</option>
              </select>
            </div>

            {/* Profit Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Profit:</label>
              <select
                value={profitFilter}
                onChange={(e) => setProfitFilter(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="lt500">&lt; $500</option>
                <option value="500-5k">$500 - $5,000</option>
                <option value="5k-25k">$5,000 - $25,000</option>
                <option value="gt25k">&gt; $25,000</option>
              </select>
            </div>

            {/* Growth Filter */}
            <div className="flex items-center space-x-2">
              <label className="text-sm text-gray-600">Growth:</label>
              <select
                value={growthFilter}
                onChange={(e) => setGrowthFilter(e.target.value)}
                className="border rounded-md px-2 py-1 text-sm focus:ring-[#FF5722] focus:border-[#FF5722]"
              >
                <option value="all">All</option>
                <option value="negative">Negative</option>
                <option value="0-10">0% - 10%</option>
                <option value="10-25">10% - 25%</option>
                <option value="gt25">&gt; 25%</option>
              </select>
            </div>

            {/* Reset Filters Button */}
            <button
              onClick={() => {
                setFinancialDateRange('24h');
                setFinancialInterval('5m');
                setRevenueFilter('all');
                setExpensesFilter('all');
                setProfitFilter('all');
                setGrowthFilter('all');
              }}
              className="ml-auto text-sm text-gray-500 hover:text-gray-700"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {/* Rest of the Financial Reports content */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Revenue</h3>
            <div className="text-2xl font-bold text-green-600">$12,345</div>
            <p className="text-sm text-gray-600">This month</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Expenses</h3>
            <div className="text-2xl font-bold text-red-600">$5,678</div>
            <p className="text-sm text-gray-600">This month</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Profit</h3>
            <div className="text-2xl font-bold text-blue-600">$6,667</div>
            <p className="text-sm text-gray-600">This month</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-medium mb-2">Growth</h3>
            <div className="text-2xl font-bold text-[#FF5722]">+15.4%</div>
            <p className="text-sm text-gray-600">vs last month</p>
          </div>
        </div>

        {/* Financial Chart */}
        <div className="border rounded-lg p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium">Revenue Breakdown</h3>
            <select className="border rounded-md px-2 py-1 text-sm">
              <option>Last 30 Days</option>
              <option>Last Quarter</option>
              <option>Last Year</option>
            </select>
          </div>
          <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
            <p className="text-gray-500">Revenue Chart</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportingAnalytics; 